/**
 * DateField — shadcn Calendar inside a Popover. Drop-in replacement for
 * `<input type="date">`. Stores the picked date as an ISO `YYYY-MM-DD` string
 * (same wire format used everywhere on the backend), so callers can keep their
 * existing `set(key, value)` pattern.
 *
 * Props
 *  - value: "YYYY-MM-DD" | "" (empty when unset)
 *  - onChange(iso) — fires with "YYYY-MM-DD" or "" if cleared
 *  - testId — applied to the trigger button
 *  - required — adds aria-required + visual "* " marker on the placeholder
 *  - min / max — optional ISO bounds; calendar disables out-of-range days
 */
import React, { useMemo } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { CalendarBlank } from "@phosphor-icons/react";

const isoFromDate = (d) => {
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const dateFromIso = (iso) => {
  if (!iso || typeof iso !== "string") return undefined;
  const [y, m, d] = iso.split("-").map(Number);
  if (!y || !m || !d) return undefined;
  return new Date(y, m - 1, d);
};

const labelFromIso = (iso) => {
  const d = dateFromIso(iso);
  if (!d) return "";
  return d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
};

export function DateField({ value, onChange, testId, required, min, max, placeholder = "Pick a date", className = "" }) {
  const selected = useMemo(() => dateFromIso(value), [value]);
  const disabled = useMemo(() => {
    const rules = [];
    if (min) rules.push({ before: dateFromIso(min) });
    if (max) rules.push({ after: dateFromIso(max) });
    return rules.length ? rules : undefined;
  }, [min, max]);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          data-testid={testId}
          aria-required={required ? "true" : undefined}
          className={`bg-white w-full justify-start font-normal ${value ? "" : "text-[var(--vs-text-secondary)]"} ${className}`}
        >
          <CalendarBlank size={14} className="mr-2 opacity-70" />
          {value ? labelFromIso(value) : (required ? `${placeholder} *` : placeholder)}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={selected}
          onSelect={(d) => onChange(isoFromDate(d))}
          disabled={disabled}
          captionLayout="dropdown"
          fromYear={1990}
          toYear={new Date().getFullYear() + 1}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
}

export default DateField;
